import { FormEvent, FunctionComponent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import {
  faGithub,
  faLinkedinIn,
  faTelegram,
} from "@fortawesome/free-brands-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useCvDataProfile } from "../../locales/cv-data";
import { useTranslate } from "../../locales/hooks";
import { reachGoal } from "../../analytics/metrika";

function stripUrl(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
}

function accountLogin(url: string): string {
  const segments = stripUrl(url).split("/");
  return segments[segments.length - 1].toLowerCase();
}

function telegramHandle(url: string): string {
  return `@${accountLogin(url)}`;
}

interface ContactRow {
  icon: IconDefinition;
  label: string;
  href: string;
}

function WriteModal({
  open,
  email,
  onClose,
}: {
  open: boolean;
  email: string;
  onClose: () => void;
}) {
  const t = useTranslate();
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open) return;

    const onKeyDownCapture = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };

    window.addEventListener("keydown", onKeyDownCapture, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKeyDownCapture, {
        capture: true,
      });
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    reachGoal("write_form_send");
    const senderLine = company ? `${name}, ${company}` : name;
    const subject = `${t("contactPage__mailSubject")} — ${senderLine}`;
    const body = `${message}\n\n—\n${senderLine}\n${replyTo}`;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      <div className="relative mx-auto mt-[10vh] w-[min(92vw,480px)] rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-4">
          <h3 className="text-base font-semibold text-slate-900">
            {t("contactPage__formTitle")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-slate-200 px-2 py-1 text-sm hover:bg-slate-50"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3 p-4">
          <label className="flex flex-col gap-1 text-sm text-slate-700">
            <span>
              {t("contactPage__formName")}{" "}
              <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-700"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm text-slate-700">
            <span>
              {t("contactPage__formCompany")}{" "}
              <span className="text-slate-400">
                ({t("contactPage__formOptional")})
              </span>
            </span>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-700"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm text-slate-700">
            <span>
              {t("contactPage__formEmail")}{" "}
              <span className="text-red-500">*</span>
            </span>
            <input
              type="email"
              required
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-700"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm text-slate-700">
            <span>
              {t("contactPage__formMessage")}{" "}
              <span className="text-red-500">*</span>
            </span>
            <textarea
              required
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-700"
            />
          </label>

          <p className="text-xs text-slate-500">{t("contactPage__formHint")}</p>

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50"
            >
              {t("contactPage__formClose")}
            </button>
            <button
              type="submit"
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              {t("contactPage__formSubmit")}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

const ContactPage: FunctionComponent = () => {
  const t = useTranslate();
  const profile = useCvDataProfile();
  const [isWriteOpen, setIsWriteOpen] = useState(false);

  const rows: ContactRow[] = [
    {
      icon: faEnvelope,
      label: profile.email,
      href: `mailto:${profile.email}`,
    },
    {
      icon: faGithub,
      label: accountLogin(profile.githubUrl),
      href: profile.githubUrl,
    },
    {
      icon: faLinkedinIn,
      label: accountLogin(profile.linkedinUrl),
      href: profile.linkedinUrl,
    },
    {
      icon: faTelegram,
      label: telegramHandle(profile.telegramUrl),
      href: profile.telegramUrl,
    },
  ];

  return (
    <div className="flex h-full min-h-0 w-full items-center justify-center overflow-y-auto px-[20px]">
      <article
        className="relative w-[min(92vw,440px)] rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_8px_24px_rgba(15,23,42,0.08)]"
        data-name="contact-card"
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[6px] rounded-t-2xl bg-slate-900" />

        <h1 className="font-['Hind',sans-serif] text-[28px] font-bold leading-tight text-slate-900">
          {profile.name} {profile.secondName}
        </h1>
        <p className="font-['Hind',sans-serif] text-[14px] text-slate-500">
          .NET Developer
        </p>

        <ul className="mt-6 flex flex-col gap-3">
          {rows.map((row) => (
            <li key={row.href}>
              <a
                href={row.href}
                target={row.href.startsWith("mailto:") ? undefined : "_blank"}
                rel={
                  row.href.startsWith("mailto:")
                    ? undefined
                    : "noopener noreferrer"
                }
                onClick={() => reachGoal("contact_click")}
                className="group flex items-center gap-3 text-slate-700 hover:text-slate-900"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition group-hover:bg-slate-900 group-hover:text-white">
                  <FontAwesomeIcon icon={row.icon} className="h-4 w-4" />
                </span>
                <span className="text-sm underline-offset-4 group-hover:underline">
                  {row.label}
                </span>
              </a>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={() => setIsWriteOpen(true)}
            className="flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            <FontAwesomeIcon icon={faPenToSquare} className="h-4 w-4" />
            {t("contactPage__writeButton")}
          </button>
        </div>
      </article>

      <WriteModal
        open={isWriteOpen}
        email={profile.email}
        onClose={() => setIsWriteOpen(false)}
      />
    </div>
  );
};

export default ContactPage;
